import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { ChevronDownIcon } from '@/components/icons/ChevronDownIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { File02Icon } from '@/components/icons/File02Icon';
import { FolderIcon } from '@/components/icons/FolderIcon';
import { COLORS } from '@/styles/tokens/colors';

const BYTES_PER_UNIT = 1024;
const SIZE_UNITS = ['bytes', 'KB', 'MB', 'GB', 'TB'];

function formatFileSize(bytes: number): string {
  let size = bytes;
  let unit = 0;
  while (size >= BYTES_PER_UNIT && unit < SIZE_UNITS.length - 1) {
    size /= BYTES_PER_UNIT;
    unit++;
  }
  return `${size.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${SIZE_UNITS[unit]}`;
}

export interface DirectoryEntry {
  name: string;
  path: string;
  isDirectory: boolean;
  sizeBytes?: number | null;
  lastModified?: Date | null;
}

interface DirectoryProps {
  loadChildren: (path: string) => Promise<DirectoryEntry[]>;
}

interface ChildrenProps extends DirectoryProps {
  path: string;
  depth: number;
  refresh: number;
}

function EntryRow({
  entry,
  depth,
  ...props
}: Omit<ChildrenProps, 'path'> & { entry: DirectoryEntry }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <TableRow>
        <TableCell sx={{ pl: 2 + depth * 3, overflowWrap: 'anywhere' }}>
          {entry.isDirectory ? (
            <Button
              variant="transparent"
              size="sm"
              aria-label={entry.name}
              aria-expanded={expanded}
              startIcon={expanded ? ChevronDownIcon : ChevronRightIcon}
              onClick={() => setExpanded(!expanded)}
            >
              <FolderIcon sx={{ mr: 1, fontSize: 18 }} />
              {entry.name}
            </Button>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" pl={1}>
              <File02Icon sx={{ fontSize: 18, flexShrink: 0 }} />
              <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
                {entry.name}
              </Typography>
            </Stack>
          )}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {entry.sizeBytes == null ? '—' : formatFileSize(entry.sizeBytes)}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {entry.lastModified
            ? new Date(entry.lastModified).toLocaleString()
            : '—'}
        </TableCell>
      </TableRow>
      {expanded && (
        <DirectoryChildren {...props} path={entry.path} depth={depth + 1} />
      )}
    </>
  );
}

function DirectoryChildren({
  path,
  depth,
  refresh,
  loadChildren,
}: ChildrenProps) {
  const [entries, setEntries] = useState<DirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    void loadChildren(path).then(
      (children) => {
        if (active) {
          setEntries(children);
          setLoading(false);
        }
      },
      () => {
        if (active) {
          setFailed(true);
          setLoading(false);
        }
      }
    );
    return () => {
      active = false;
    };
  }, [loadChildren, path, refresh]);

  let message: string | undefined;
  if (loading) message = 'Loading…';
  else if (failed)
    message =
      entries.length > 0
        ? 'Could not refresh this folder. Showing previously loaded entries. Select Refresh to try again.'
        : 'Could not load this folder. Select Refresh to try again.';
  else if (entries.length === 0) message = 'This folder is empty.';

  return (
    <>
      {message && (
        <TableRow>
          <TableCell colSpan={3} sx={{ pl: 3 + depth * 3 }}>
            <Typography variant="body-xs" role={failed ? 'alert' : 'status'}>
              {message}
            </Typography>
          </TableCell>
        </TableRow>
      )}
      {/* Keep mounted rows through refresh failures so expanded folders stay open. */}
      {entries.map((entry) => (
        <EntryRow
          key={entry.path}
          entry={entry}
          depth={depth}
          refresh={refresh}
          loadChildren={loadChildren}
        />
      ))}
    </>
  );
}

// Independent of page routing so project jobs can use this directory too.
export function ManagedSFTPDirectory({ loadChildren }: DirectoryProps) {
  const [refresh, setRefresh] = useState(0);
  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body-xs">
          Expand a folder to check its files. Folder size and modification time
          are not available.
        </Typography>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setRefresh(refresh + 1)}
        >
          Refresh
        </Button>
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="Managed SFTP files">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '55%' }}>Name</TableCell>
              <TableCell align="right">Size</TableCell>
              <TableCell>Last modified</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <DirectoryChildren
              path=""
              depth={0}
              refresh={refresh}
              loadChildren={loadChildren}
            />
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
}
